/* Phase 36 — Drink Intelligence page (sidebar entry point). */
import React, { useEffect, useMemo, useState } from "react";
import { Search, Martini, Sparkles } from "lucide-react";
import api, { formatApiError } from "../api";
import { Input, Card, EmptyState, Spinner } from "../components/ui/Primitives";
import DrinkWizard from "../components/DrinkWizard";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";

export default function DrinkIntelligencePage() {
  const { user } = useAuth();
  const [cocktails, setCocktails] = useState([]);
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);

  const hasModule = (user?.modules || []).includes("drink");

  useEffect(() => {
    api.get("/cocktails").then(({ data }) => setCocktails(data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? cocktails.filter((c) => c.name?.toLowerCase().includes(q)) : cocktails;
  }, [cocktails, search]);

  const active = cocktails.find((c) => c.id === activeId);

  if (!hasModule) {
    return <DrinkLockedState plan={user?.plan} />;
  }

  return (
    <div className="space-y-6" data-testid="drink-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Drink Intelligence</h1>
          <p className="text-sm text-ink-muted mt-1">
            Professionelle Präsentations-Vorschläge mit ultra-realistischen Fotos für deine Cocktails.
          </p>
        </div>
      </header>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        <Card className="p-3 space-y-3 lg:max-h-[80vh] lg:overflow-y-auto">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-ink-muted" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cocktail suchen…"
              className="pl-8"
              data-testid="drink-cocktail-search"
            />
          </div>
          {loading ? (
            <div className="py-6 text-center"><Spinner /></div>
          ) : filtered.length === 0 ? (
            <EmptyState icon={Martini} title="Keine Cocktails" description="Lege zuerst einen Cocktail an." />
          ) : (
            <div className="space-y-1">
              {filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActiveId(c.id)}
                  data-testid={`drink-cocktail-pick-${c.id}`}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${
                    activeId === c.id ? "bg-brand/10 text-brand" : "hover:bg-white/5"
                  }`}
                >
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-[11px] text-ink-muted truncate">{c.category}</div>
                </button>
              ))}
            </div>
          )}
        </Card>

        <div>
          {active ? (
            <Card className="p-5">
              <div className="mb-4">
                <div className="text-xs uppercase tracking-wide text-ink-muted">Ausgewählter Cocktail</div>
                <div className="text-lg font-medium">{active.name}</div>
              </div>
              <DrinkWizard cocktail={active} />
            </Card>
          ) : (
            <Card className="p-12">
              <EmptyState
                icon={Sparkles}
                title="Wähle einen Cocktail aus"
                description="Drink Intelligence erstellt aus den vorhandenen Zutaten Präsentations-Vorschläge und Fotos."
              />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function DrinkLockedState({ plan }) {
  const isBar = plan === "bar";
  const buyAddon = async () => {
    try {
      const { data } = await api.post("/billing/drink-addon/checkout", { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="max-w-2xl mx-auto py-12 space-y-6" data-testid="drink-locked">
      <div className="text-center space-y-2">
        <Martini size={36} className="mx-auto text-brand" />
        <h1 className="text-2xl md:text-3xl font-semibold">Drink Intelligence</h1>
        <p className="text-sm text-ink-muted">
          Professionelle Präsentations-Vorschläge und ultra-realistische Cocktailfotos für deine Karte.
        </p>
      </div>
      {isBar ? (
        <Card className="p-6 space-y-4">
          <div className="text-sm">
            Drink Intelligence ist im <span className="font-medium">Bar-Plan</span> nicht enthalten. Du hast zwei Optionen:
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => window.location.assign("/billing")}
              className="text-left p-4 rounded-lg border border-white/10 hover:border-brand transition"
              data-testid="drink-upgrade-pro"
            >
              <div className="text-sm font-semibold">Auf Pro upgraden</div>
              <div className="text-xs text-ink-muted mt-1">€129/Monat · 60 AI-Credits + 50 Drink-Credits inklusive · alle Module</div>
            </button>
            <button
              type="button"
              onClick={buyAddon}
              className="text-left p-4 rounded-lg border border-brand/40 bg-brand/5 hover:bg-brand/10 transition"
              data-testid="drink-buy-addon"
            >
              <div className="text-sm font-semibold">Add-on buchen</div>
              <div className="text-xs text-ink-muted mt-1">€19/Monat · 20 Drink-Credits/Monat · ohne Plan-Wechsel</div>
            </button>
          </div>
        </Card>
      ) : (
        <Card className="p-6 text-sm text-ink-muted">
          Drink Intelligence ist in deinem aktuellen Plan nicht verfügbar.
        </Card>
      )}
    </div>
  );
}
